import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Container,
  Button,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  useMediaQuery,
  useTheme,
  keyframes,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import logo from '../../assets/logo.png';
import heroBg from '../../assets/hero1.png';

// Keyframe for auto-scroll animation
const scroll = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const Header = () => {
  const { isAuthenticated, user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEls, setAnchorEls] = useState({});
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState({});

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
      setMobileDropdownOpen({});
    }
  }, [isMobile]);

  if (loading) {
    return <div>Loading...</div>; // or a spinner component
  }

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  // Updated: Now handles hover for desktop
  const handleMenuOpen = (event, menuName) => {
    setAnchorEls({ ...anchorEls, [menuName]: event.currentTarget });
  };

  const handleMenuClose = (menuName) => {
    setAnchorEls({ ...anchorEls, [menuName]: null });
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMobileDropdown = (menuName) => {
    setMobileDropdownOpen({
      ...mobileDropdownOpen,
      [menuName]: !mobileDropdownOpen[menuName],
    });
  };

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setMobileDropdownOpen({});
  };

  // Complete navigation items from original Header
  const navigationItems = isAuthenticated? [
    {
      label: 'About Us',
      type: 'dropdown',
      items: [
        { label: 'Who We Are', path: '/who-we-are' },
        { label: 'International Affiliations', path: '/international-affiliations' },
        { label: 'Public Information Officer', path: '/public-information-officer' },
        { label: 'List of Technical Officials', path: '/list-of-technical-officials' },
        { label: 'List of Coaches', path: '/list-of-coaches' },
      ],
    },
    {
      label: 'Result',
      type: 'dropdown',
      items: [
        { label: 'Latest Results', path: '/latest-results' },
        { label: 'National Records', path: '/national-records' },
      ],
    },
    {
      label: 'National Camps',
      type: 'dropdown',
      items: [
        { label: 'Current Camps', path: '/current-camps' },
        { label: 'Past Camps', path: '/past-camps' },
      ],
    },
    {
      label: 'Awardees',
      type: 'dropdown',
      items: [
        { label: 'National Awards', path: '/national-awards' },
        { label: 'International Awards', path: '/international-awards' },
      ],
    },
    {
      label: 'Documents',
      type: 'dropdown',
      items: [
        { label: 'Forms', path: '/forms' },
        { label: 'Rulebooks', path: '/rulebooks' },
        { label: 'Circulars', path: '/circulars' },
      ],
    },
    {
      label: 'News',
      path: '/news',
      type: 'link',
    },
    {
      label: 'Competitions',
      path: '/competitions',
      type: 'link',
    },
    {
      label: 'Dashboard',
      path: '/dashboard',
      type: 'link',
    },
  ]: [
    {
      label: 'About Us',
      type: 'dropdown',
      items: [
        { label: 'Who We Are', path: '/who-we-are' },
        { label: 'International Affiliations', path: '/international-affiliations' },
        { label: 'Public Information Officer', path: '/public-information-officer' },
        { label: 'List of Technical Officials', path: '/list-of-technical-officials' },
        { label: 'List of Coaches', path: '/list-of-coaches' },
      ],
    },
    {
      label: 'Result',
      type: 'dropdown',
      items: [
        { label: 'Latest Results', path: '/latest-results' },
        { label: 'National Records', path: '/national-records' },
      ],
    },
    {
      label: 'National Camps',
      type: 'dropdown',
      items: [
        { label: 'Current Camps', path: '/current-camps' },
        { label: 'Past Camps', path: '/past-camps' },
      ],
    },
    {
      label: 'Awardees',
      type: 'dropdown',
      items: [
        { label: 'National Awards', path: '/national-awards' },
        { label: 'International Awards', path: '/international-awards' },
      ],
    },
    {
      label: 'Documents',
      type: 'dropdown',
      items: [
        { label: 'Forms', path: '/forms' },
        { label: 'Rulebooks', path: '/rulebooks' },
        { label: 'Circulars', path: '/circulars' },
      ],
    },
    {
      label: 'News',
      path: '/news',
      type: 'link',
    },
    {
      label: 'Competitions',
      path: '/competitions',
      type: 'link',
    },
    {
      label: 'Registration',
      type: 'dropdown',
      items: [
        { label: 'Athlete Registration', path: '/register' },
        { label: 'Login', path: '/login' },
      ],
      // Dashboard will be added conditionally based on authentication
    },
  ];

  return (
    <>
      {/* Top Info Bar - Announcement Bar with Auto-Scroll on Mobile */}
      <Box
        sx={{
          backgroundColor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          py: 1.25,
          fontFamily: '"Oswald", sans-serif',
          overflow: 'hidden', // Hide overflow for scroll effect
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2.5,
              flexDirection: { xs: 'row', md: 'row' }, // Changed to row for mobile scroll
              textAlign: { xs: 'center', md: 'left' },
              // Mobile: Enable auto-scroll
              '@media (max-width: 899px)': {
                animation: `${scroll} 15s linear infinite`,
                width: 'max-content',
                gap: 5,
                '&:hover': {
                  animationPlayState: 'paused',
                },
              },
            }}
          >
            {/* Email Section */}
            <Box sx={{ flex: { xs: 'none', md: 1 }, whiteSpace: 'nowrap' }}>
              <Typography
                component="a"
                href="mailto:weightlifting@weightliftingindia.in"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: '#333',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 400,
                  fontFamily: '"Oswald", sans-serif',
                  '&:hover': {
                    color: '#291e6c',
                  },
                }}
              >
                <EmailIcon sx={{ fontSize: '13px' }} />
                weightlifting@weightliftingindia.in
              </Typography>
            </Box>

            {/* Announcement Text */}
            <Box sx={{ flex: { xs: 'none', md: 3 }, textAlign: 'center', whiteSpace: 'nowrap' }}>
              <Typography
                sx={{
                  margin: 0,
                  fontWeight: 500,
                  color: '#333',
                  fontSize: '13px',
                  fontFamily: '"Oswald", sans-serif',
                }}
              >
                Athlete Registration is now OPEN!
              </Typography>
            </Box>

            {/* Phone Section */}
            <Box sx={{ flex: { xs: 'none', md: 1 }, textAlign: { xs: 'center', md: 'right' }, whiteSpace: 'nowrap' }}>
              <Typography
                component="a"
                href="tel:01204541605"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  color: '#333',
                  textDecoration: 'none',
                  fontSize: '13px',
                  fontWeight: 400,
                  fontFamily: '"Oswald", sans-serif',
                  '&:hover': {
                    color: '#291e6c',
                  },
                }}
              >
                <PhoneIcon sx={{ fontSize: '13px' }} />
                0120-4541605
              </Typography>
            </Box>

            {/* Duplicate content for seamless loop on mobile */}
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                gap: 5,
                alignItems: 'center',
              }}
            >
              <Box sx={{ whiteSpace: 'nowrap' }}>
                <Typography
                  component="a"
                  href="mailto:weightlifting@weightliftingindia.in"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: '#333',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    fontFamily: '"Oswald", sans-serif',
                    '&:hover': {
                      color: '#291e6c',
                    },
                  }}
                >
                  <EmailIcon sx={{ fontSize: '13px' }} />
                  weightlifting@weightliftingindia.in
                </Typography>
              </Box>
              <Box sx={{ whiteSpace: 'nowrap' }}>
                <Typography
                  sx={{
                    margin: 0,
                    fontWeight: 500,
                    color: '#333',
                    fontSize: '13px',
                    fontFamily: '"Oswald", sans-serif',
                  }}
                >
                  Last date for submitting applications for various awards:4th Nov'25
                </Typography>
              </Box>
              <Box sx={{ whiteSpace: 'nowrap' }}>
                <Typography
                  component="a"
                  href="tel:01204541605"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: '#333',
                    textDecoration: 'none',
                    fontSize: '13px',
                    fontWeight: 400,
                    fontFamily: '"Oswald", sans-serif',
                    '&:hover': {
                      color: '#291e6c',
                    },
                  }}
                >
                  <PhoneIcon sx={{ fontSize: '13px' }} />
                  0120-4541605
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Title Bar with Hero Background */}
      <Box
        sx={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          py: { xs: 2.5, sm: 3.75 },
          minHeight: { xs: '60px', sm: '80px', md: '100px' },
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 700,
              textAlign: 'center',
              margin: 0,
              letterSpacing: { xs: '0.5px', sm: '1px', md: '2px' },
              textTransform: 'uppercase',
              fontFamily: '"Oswald", sans-serif',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              lineHeight: 1.2,
              fontSize: {
                xs: '16px',
                sm: '20px',
                md: '24px',
                lg: '28px',
                xl: '32px',
              },
            }}
          >
            INDIAN WEIGHTLIFTING FEDERATION
          </Typography>
        </Container>
      </Box>

      {/* Main Navigation Bar - Sticky Only */}
      <AppBar
        position="sticky"
        sx={{
          background: 'linear-gradient(135deg, #FFFFFF 0%, #251B61 100%)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          borderRadius: 0,
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.25,
              gap: 3.75,
            }}
          >
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                flexShrink: 0,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="IWLF"
                sx={{
                  height: { xs: '50px', sm: '55px', md: '60px', lg: '65px', xl: '75px' },
                  width: 'auto',
                  display: 'block',
                }}
              />
            </Box>

            {/* Desktop Navigation with Hover Dropdowns */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {navigationItems.map((item, index) => (
                  <Box key={index}>
                    {item.type === 'link' ? (
                      <Button
                        component={Link}
                        to={item.path}
                        sx={{
                          color: '#ffffff',
                          fontSize: { lg: '13px', xl: '15px' },
                          fontWeight: 600,
                          fontFamily: '"Oswald", sans-serif',
                          textTransform: 'capitalize',
                          letterSpacing: '0.3px',
                          px: { lg: 1.25, xl: 1.875 },
                          py: 2.5,
                          '&:hover': {
                            color: '#FEED00',
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        {item.label}
                      </Button>
                    ) : (
                      <>
                        <Button
                          // Changed from onClick to onMouseEnter for hover
                          onMouseEnter={(e) => handleMenuOpen(e, item.label)}
                          endIcon={<ExpandMoreIcon sx={{ fontSize: '9px' }} />}
                          sx={{
                            color: '#ffffff',
                            fontSize: { lg: '13px', xl: '15px' },
                            fontWeight: 600,
                            fontFamily: '"Oswald", sans-serif',
                            textTransform: 'capitalize',
                            letterSpacing: '0.3px',
                            px: { lg: 1.25, xl: 1.875 },
                            py: 2.5,
                            '&:hover': {
                              color: '#FEED00',
                              backgroundColor: 'transparent',
                            },
                            '& .MuiButton-endIcon': {
                              ml: 0.625,
                              transition: 'transform 0.3s ease',
                            },
                            '&:hover .MuiButton-endIcon': {
                              transform: 'rotate(180deg)',
                            },
                          }}
                        >
                          {item.label}
                        </Button>
                        <Menu
                          anchorEl={anchorEls[item.label]}
                          open={Boolean(anchorEls[item.label])}
                          onClose={() => handleMenuClose(item.label)}
                          // Added MenuListProps to handle hover behavior
                          MenuListProps={{
                            onMouseLeave: () => handleMenuClose(item.label),
                          }}
                          // Added slotProps to handle hover on menu container
                          slotProps={{
                            paper: {
                              onMouseLeave: () => handleMenuClose(item.label),
                            },
                          }}
                          PaperProps={{
                            sx: {
                              minWidth: '240px',
                              mt: 0.5,
                              borderTop: '3px solid #291e6c',
                              borderRadius: '0 0 0px 0px',
                              fontFamily: '"Oswald", sans-serif',
                            },
                          }}
                        >
                          {item.items.map((subItem, subIndex) => (
                            <MenuItem
                              key={subIndex}
                              component={Link}
                              to={subItem.path}
                              onClick={() => handleMenuClose(item.label)}
                              sx={{
                                py: 1.5,
                                px: 2.5,
                                color: '#333',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: '"Oswald", sans-serif',
                                textTransform: 'capitalize',
                                borderLeft: '3px solid transparent',
                                '&:hover': {
                                  color: '#291e6c',
                                  backgroundColor: '#f5f5f5',
                                  borderLeftColor: '#291e6c',
                                  pl: 3.125,
                                },
                              }}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </Box>
                ))}

                {/* Sign In/Out Button - Fixed Logic */}
                {isAuthenticated ? (
                  <Button
                    onClick={handleLogout}
                    sx={{
                      ml: 1.875,
                      px: 3.5,
                      py: 1.25,
                      backgroundColor: 'transparent',
                      color: '#ffffff',
                      border: '2px solid #ffffff',
                      borderRadius: '25px',
                      fontSize: '14px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontFamily: '"Oswald", sans-serif',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#ffffff',
                        color: '#291e6c',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Sign Out
                  </Button>
                ) : (
                  <Button
                    component={Link}
                    to="/login"
                    sx={{
                      ml: 1.875,
                      px: 3.5,
                      py: 1.25,
                      backgroundColor: 'transparent',
                      color: '#ffffff',
                      border: '2px solid #ffffff',
                      borderRadius: '25px',
                      fontSize: '14px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      fontFamily: '"Oswald", sans-serif',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#ffffff',
                        color: '#291e6c',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    Sign In
                  </Button>
                )}
              </Box>
            )}

            {/* Mobile Menu Toggle */}
            {isMobile && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={toggleMobileMenu}
                sx={{ color: '#ffffff' }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: '300px',
            background: 'linear-gradient(180deg, #291e6c 0%, #1f1750 100%)',
            pt: 2,
          },
        }}
      >
        <List>
          {navigationItems.map((item, index) => (
            <Box key={index}>
              {item.type === 'link' ? (
                <ListItem disablePadding>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    onClick={closeAllMenus}
                    sx={{
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      py: 2,
                      px: 2.5,
                    }}
                  >
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        sx: {
                          color: '#ffffff',
                          fontSize: '15px',
                          fontWeight: 600,
                          fontFamily: '"Oswald", sans-serif',
                          textTransform: 'capitalize',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ) : (
                <>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => toggleMobileDropdown(item.label)}
                      sx={{
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        py: 2,
                        px: 2.5,
                      }}
                    >
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          sx: {
                            color: '#ffffff',
                            fontSize: '15px',
                            fontWeight: 600,
                            fontFamily: '"Oswald", sans-serif',
                            textTransform: 'capitalize',
                          },
                        }}
                      />
                      {mobileDropdownOpen[item.label] ? (
                        <ExpandLessIcon sx={{ color: '#ffffff' }} />
                      ) : (
                        <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                  <Collapse in={mobileDropdownOpen[item.label]} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{
                        borderLeft: '3px solid #FEED00',
                        ml: 2.5,
                        background: 'rgba(255, 255, 255, 0.05)',
                      }}
                    >
                      {item.items.map((subItem, subIndex) => (
                        <ListItemButton
                          key={subIndex}
                          component={Link}
                          to={subItem.path}
                          onClick={closeAllMenus}
                          sx={{
                            py: 1.5,
                            px: 2.5,
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.1)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              sx: {
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: '"Oswald", sans-serif',
                                textTransform: 'capitalize',
                                '&:hover': {
                                  color: '#FEED00',
                                },
                              },
                            }}
                          />
                        </ListItemButton>
                      ))}
                      {/* Add Dashboard conditionally in mobile menu */}
                      {item.label === 'Registration' && isAuthenticated && (
                        <ListItemButton
                          component={Link}
                          to="/dashboard"
                          onClick={closeAllMenus}
                          sx={{
                            py: 1.5,
                            px: 2.5,
                            '&:hover': {
                              background: 'rgba(255, 255, 255, 0.1)',
                            },
                          }}
                        >
                          <ListItemText
                            primary="Dashboard"
                            primaryTypographyProps={{
                              sx: {
                                color: '#ffffff',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: '"Oswald", sans-serif',
                                textTransform: 'capitalize',
                                '&:hover': {
                                  color: '#FEED00',
                                },
                              },
                            }}
                          />
                        </ListItemButton>
                      )}
                    </List>
                  </Collapse>
                </>
              )}
            </Box>
          ))}

          {/* Mobile Sign In/Out Button - Fixed Logic */}
          <ListItem sx={{ px: 2.5, py: 1.875 }}>
            {isAuthenticated ? (
              <Button
                fullWidth
                onClick={handleLogout}
                sx={{
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: '"Oswald", sans-serif',
                  py: 1.25,
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    color: '#291e6c',
                  },
                }}
              >
                Sign Out
              </Button>
            ) : (
              <Button
                fullWidth
                component={Link}
                to="/login"
                onClick={closeAllMenus}
                sx={{
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  border: '2px solid #ffffff',
                  borderRadius: '25px',
                  fontSize: '14px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  fontFamily: '"Oswald", sans-serif',
                  py: 1.25,
                  '&:hover': {
                    backgroundColor: '#ffffff',
                    color: '#291e6c',
                  },
                }}
              >
                Sign In
              </Button>
            )}
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default Header;
